/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useState, useRef } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { OG_CONSOLIDATION_USER_STATE_STORAGE_KEY } from '@jira/router-resources__eoc-og-consolidation-user-state/src/ogConsolidationUserStateStorageKey.constants.tsx';
import type { OgConsolidationUserState } from '@jira/router-resources__eoc-og-consolidation-user-state/src/OgConsolidationUserState.types.tsx';
import { consolidationUserStateResource } from '@jira/router-resources__eoc-og-consolidation-user-state/src/consolidationUserStateResource.tsx';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useResource } from '@atlassian/react-resource-router';

const errorFlagTitle = defineMessage({
	id: 'eoc-changeboarding.controllers.use-og-consolidation-user-state.error-flag-title',
	defaultMessage: 'Something went wrong while updating user properties.',
	description: 'Helper text for updating user properties error flag',
});

export const useOgConsolidationUserState = (): {
	data: OgConsolidationUserState | null;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	update: (newState: Partial<OgConsolidationUserState>) => Promise<any>;
	error: unknown;
	reopenTransitionLozenge: () => void;
} => {
	const errorRef = useRef<unknown>();
	const loadingRef = useRef(false);
	const { showFlag } = useFlagsService();
	const {
		data,
		update: updateTransitionResource,
		error: transitionResourceError,
	} = useResource(consolidationUserStateResource);

	const { formatMessage } = useIntl();
	const [userPropertiesError, setUserPropertiesError] = useState<Error>();

	errorRef.current = userPropertiesError || transitionResourceError;

	const { atlassianAccountId: accountId } = useTenantContext();

	const update = useCallback(
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		(newState: Partial<OgConsolidationUserState>): Promise<any> => {
			if (errorRef.current) {
				// If there is an error on resource or update, we don't want to update the state
				return Promise.reject(errorRef.current);
			}

			// oxlint-disable-next-line typescript/consistent-type-assertions
			let updatedData = {} as OgConsolidationUserState;
			updateTransitionResource((oldState) => {
				updatedData = {
					...oldState,
					...newState,
				};
				return updatedData;
			});

			if (accountId) {
				if (loadingRef.current) {
					const err = new Error('User properties is loading');
					fireErrorAnalytics({
						meta: {
							id: 'useOgConsolidationUserPreference-update',
							packageName: 'jiraEocChangeboarding',
							teamName: 'alexstrasza',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
					setUserPropertiesError(err);
					return Promise.reject(err);
				}

				loadingRef.current = true;
				return setUserProperties(
					accountId,
					OG_CONSOLIDATION_USER_STATE_STORAGE_KEY,
					JSON.stringify(updatedData),
				)
					.catch((err) => {
						setUserPropertiesError(new Error('Error occured while setting user properties'));

						showFlag({
							type: 'error',
							isAutoDismiss: true,
							title: formatMessage(errorFlagTitle),
							// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
							messageId:
								'eoc-changeboarding.controllers.use-og-consolidation-user-state.show-flag.error.jira-modal',
							messageType: 'transactional',
						});

						fireErrorAnalytics({
							meta: {
								id: 'useOgConsolidationUserPreference-update',
								packageName: 'jiraEocChangeboarding',
								teamName: 'alexstrasza',
							},
							error: err,
							sendToPrivacyUnsafeSplunk: true,
						});
					})
					.finally(() => {
						loadingRef.current = false;
					});
			}

			return Promise.resolve();
		},
		[updateTransitionResource, accountId, formatMessage, showFlag],
	);

	const reopenTransitionLozenge = useCallback((): void => {
		update({ isTransitionHomeHidden: false });
	}, [update]);

	return {
		data,
		update,
		error: errorRef.current,
		reopenTransitionLozenge,
	};
};
